/**
 * App.jsx - AI Fighter Multi-Utility Platform
 */
import React, { useEffect, Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { ShopProvider } from './contexts/ShopContext';
import { analyticsAPI } from './services/api';
import Layout from './components/layout/Layout';
import RequireAuth from './components/auth/RequireAuth';

// Eagerly load only the entry route — everything else is split.
import HomePage from './pages/HomePage';

// Route-level code splitting: each page becomes its own chunk and is fetched
// only when the user actually navigates there. Cuts the 3.4 MB main bundle by ~80%.
const LoginPage              = lazy(() => import('./pages/LoginPage'));
const RegisterPage           = lazy(() => import('./pages/RegisterPage'));
const LoginSuccessPage       = lazy(() => import('./pages/LoginSuccessPage'));
const OAuthRedirectPage      = lazy(() => import('./pages/OAuthRedirectPage'));
const VerifyEmailPage        = lazy(() => import('./pages/VerifyEmailPage'));
const ForgotPasswordPage     = lazy(() => import('./pages/ForgotPasswordPage'));
const ResetPasswordPage      = lazy(() => import('./pages/ResetPasswordPage'));
const CreateBattle           = lazy(() => import('./pages/CreateBattle'));
const BattleView             = lazy(() => import('./pages/BattleView'));
const BattleList             = lazy(() => import('./pages/BattleList'));
const TermsPage              = lazy(() => import('./pages/TermsPage'));
const PrivacyPage            = lazy(() => import('./pages/PrivacyPage'));
const MyPage                 = lazy(() => import('./pages/MyPage'));
const ShareRedirect          = lazy(() => import('./pages/ShareRedirect'));
const SearchPage             = lazy(() => import('./pages/SearchPage'));
const FeatureLabPage         = lazy(() => import('./pages/FeatureLabPage'));
const AdminOpsPage           = lazy(() => import('./pages/AdminOpsPage'));
const PartnerHubPage         = lazy(() => import('./pages/PartnerHubPage'));
const CommunityPage          = lazy(() => import('./pages/CommunityPage'));
const VisionLabPage          = lazy(() => import('./pages/VisionLabPage'));
const ControlHubPage         = lazy(() => import('./pages/ControlHubPage'));
const FruitMarketPage        = lazy(() => import('./pages/FruitMarketPage'));
const AiToolsPage            = lazy(() => import('./pages/AiToolsPage'));
const AgentChatPage          = lazy(() => import('./pages/AgentChatPage'));
const CommandCenterPage      = lazy(() => import('./pages/CommandCenterPage'));
const TheBridgePage          = lazy(() => import('./pages/TheBridgePage'));
const TrafficMonitorPage     = lazy(() => import('./pages/TrafficMonitorPage'));
const DispatchLogisticsPage  = lazy(() => import('./pages/DispatchLogisticsPage'));
const FruitDeliveryStatusPage= lazy(() => import('./pages/FruitDeliveryStatusPage'));
const FarmingPage            = lazy(() => import('./pages/FarmingPage'));
const AgriIntelligencePage   = lazy(() => import('./pages/AgriIntelligencePage'));
const AgriCalendarPage       = lazy(() => import('./pages/AgriCalendarPage'));
const ArxivDashboardPage     = lazy(() => import('./pages/ArxivDashboardPage'));
const KamisDashboardPage     = lazy(() => import('./pages/KamisDashboardPage'));
const DigitalTwinPage        = lazy(() => import('./pages/DigitalTwinPage'));
const ErpPage                = lazy(() => import('./pages/ErpPage'));
const PageEditorPage         = lazy(() => import('./pages/PageEditorPage'));
const TechPortfolioPage      = lazy(() => import('./pages/TechPortfolioPage'));
const GeneratedProductPage   = lazy(() => import('./pages/GeneratedProductPage'));
const ProductShowcasePage    = lazy(() => import('./pages/ProductShowcasePage'));
const InquiryPage            = lazy(() => import('./pages/InquiryPage'));
const CartPage               = lazy(() => import('./pages/CartPage'));
const CheckoutPage           = lazy(() => import('./pages/CheckoutPage'));
const LimitedAdminPage       = lazy(() => import('./pages/LimitedAdminPage'));
const AegisSimulatorPage     = lazy(() => import('./pages/AegisSimulatorPage'));
const CliPage                = lazy(() => import('./pages/CliPage'));
const DiscordGameBotPage     = lazy(() => import('./pages/DiscordGameBotPage'));
const DiscordGameBotAdminWikiPage = lazy(() => import('./pages/DiscordGameBotAdminWikiPage'));
const GameForestPage         = lazy(() => import('./pages/GameForestPage'));
const Interview              = lazy(() => import('./pages/Interview'));
const InterviewResult        = lazy(() => import('./pages/InterviewResult'));
const Musician               = lazy(() => import('./pages/Musician'));
const MusicianProfile        = lazy(() => import('./pages/MusicianProfile'));
const MusicianSearch         = lazy(() => import('./pages/MusicianSearch'));
const MusicianJobs           = lazy(() => import('./pages/MusicianJobs'));
const MusicianProjects       = lazy(() => import('./pages/MusicianProjects'));
const MusicianSpaces         = lazy(() => import('./pages/MusicianSpaces'));
const MusicianAccessibility  = lazy(() => import('./pages/MusicianAccessibility'));
const NotFoundPage           = lazy(() => import('./pages/NotFoundPage'));

// Error Boundary: 인앱 브라우저 등에서 JS 에러 시 검은 화면 방지
class ErrorBoundary extends React.Component {
  state = { hasError: false };
  static getDerivedStateFromError() { return { hasError: true }; }
  render() {
    if (this.state.hasError) {
      return (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', padding: 24, textAlign: 'center' }}>
          <div>
            <h2 style={{ fontSize: 20, fontWeight: 'bold', marginBottom: 8 }}>페이지를 불러올 수 없습니다</h2>
            <p style={{ color: '#888', marginBottom: 16 }}>브라우저를 새로고침하거나 외부 브라우저로 열어주세요.</p>
            <button onClick={() => window.location.reload()} style={{ padding: '10px 24px', background: '#000', color: '#fff', border: 'none', borderRadius: 24, fontSize: 15, cursor: 'pointer' }}>
              새로고침
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

/* 페이지 뷰 자동 추적 */
function PageTracker() {
  const location = useLocation();
  useEffect(() => {
    analyticsAPI.track({ action: 'page_view', page: location.pathname });
  }, [location.pathname]);
  return null;
}

// Subtle loader shown while route chunks are fetched.
function RouteFallback() {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '40vh' }} aria-live="polite" aria-busy="true">
      <div style={{ width: 32, height: 32, border: '2px solid #888', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

function App() {
  return (
    <ErrorBoundary>
    <Router>
      <PageTracker />
      <AuthProvider>
      <ShopProvider>
        <Layout>
          <Suspense fallback={<RouteFallback />}>
            <Routes>
              <Route path="/" element={<HomePage />} />
              <Route path="/login" element={<LoginPage />} />
              <Route path="/register" element={<RegisterPage />} />
              <Route path="/login/:provider" element={<OAuthRedirectPage />} />
              <Route path="/verify-email" element={<VerifyEmailPage />} />
              <Route path="/login-success" element={<LoginSuccessPage />} />
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />
              <Route path="/reset-password" element={<ResetPasswordPage />} />

              {/* Feed */}
              <Route path="/search" element={<SearchPage />} />
              <Route path="/rescue-lab" element={<FeatureLabPage />} />
              <Route path="/ops-console" element={<AdminOpsPage />} />
              <Route path="/control-hub" element={<ControlHubPage />} />
              <Route path="/integrated-center" element={<PartnerHubPage />} />
              <Route path="/community" element={<CommunityPage />} />
              <Route path="/vision-lab" element={<VisionLabPage />} />
              <Route path="/fruit-market" element={<FruitMarketPage />} />
              <Route path="/ai-tools" element={<AiToolsPage />} />
              <Route path="/agent" element={<AgentChatPage />} />
              <Route path="/command-center" element={<CommandCenterPage />} />
              <Route path="/the-bridge" element={<TheBridgePage />} />
              <Route path="/traffic-monitor" element={<TrafficMonitorPage />} />
              <Route path="/dispatch-logistics" element={<DispatchLogisticsPage />} />
              <Route path="/farming" element={<FarmingPage />} />
              <Route path="/agri-intelligence" element={<AgriIntelligencePage />} />
              <Route path="/agri-calendar" element={<AgriCalendarPage />} />
              <Route path="/arxiv" element={<ArxivDashboardPage />} />
              <Route path="/market" element={<KamisDashboardPage />} />
              <Route path="/digital-twin" element={<DigitalTwinPage />} />
              <Route path="/erp" element={<ErpPage />} />
              <Route path="/page-editor" element={<PageEditorPage />} />
              <Route path="/p/:slug" element={<GeneratedProductPage />} />
              <Route path="/product/:slug" element={<GeneratedProductPage />} />
              <Route path="/products" element={<ProductShowcasePage />} />
              <Route path="/inquiry" element={<InquiryPage />} />
              <Route path="/consultation" element={<InquiryPage />} />
              <Route path="/cart" element={<CartPage />} />
              <Route path="/checkout" element={<CheckoutPage />} />
              <Route path="/admin-lite" element={<LimitedAdminPage />} />
              <Route path="/aegis" element={<AegisSimulatorPage />} />
              <Route path="/cli" element={<CliPage />} />
              <Route path="/discord" element={<DiscordGameBotPage />} />
              <Route path="/discord-gamebot" element={<DiscordGameBotPage />} />
              <Route path="/discord-gamebot/admin-guide" element={<DiscordGameBotAdminWikiPage />} />
              <Route path="/discord-gamebot/wiki" element={<DiscordGameBotAdminWikiPage />} />
              <Route path="/game/forest" element={<RequireAuth><GameForestPage /></RequireAuth>} />
              <Route path="/tech" element={<TechPortfolioPage />} />
              <Route path="/delivery/:routeCode" element={<FruitDeliveryStatusPage />} />
              <Route path="/battles" element={<BattleList />} />
              <Route path="/battles/:id" element={<BattleView />} />
              <Route path="/battle/:id" element={<BattleView />} />
              <Route path="/share/:shareCode" element={<ShareRedirect />} />
              <Route path="/battle/share/:shareCode" element={<ShareRedirect />} />

              {/* Legal */}
              <Route path="/terms" element={<TermsPage />} />
              <Route path="/privacy" element={<PrivacyPage />} />

              {/* These pages handle auth internally */}
              <Route path="/battles/create" element={<CreateBattle />} />
              <Route path="/mypage" element={<MyPage />} />
              {/* Migrated from mobidicsaju.com */}
              <Route path="/interview" element={<RequireAuth><Interview /></RequireAuth>} />
              <Route path="/interview/result" element={<RequireAuth><InterviewResult /></RequireAuth>} />
              <Route path="/musician" element={<Musician />} />
              <Route path="/musician/profile" element={<RequireAuth><MusicianProfile /></RequireAuth>} />
              <Route path="/musician/search" element={<MusicianSearch />} />
              <Route path="/musician/jobs" element={<MusicianJobs />} />
              <Route path="/musician/projects" element={<MusicianProjects />} />
              <Route path="/musician/spaces" element={<MusicianSpaces />} />
              <Route path="/musician/accessibility" element={<MusicianAccessibility />} />
              <Route path="*" element={<NotFoundPage />} />
            </Routes>
          </Suspense>
        </Layout>
      </ShopProvider>
      </AuthProvider>
    </Router>
    </ErrorBoundary>
  );
}

export default App;
